'use strict';
import get from 'lodash/get';
import {AjaxFactory, getPageFilterParams, HttpMethod, serializeFilterOptions} from '../../_libs/redux-observable-list';
import {isCordovaBuild, ZIP_API_URL} from '../../config/constants';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {ASSET_PROJECT_COLLECTION} from '../../projects/domain/AssetProjectDomain';
import {PROJECT_COLLECTION} from '../../projects/domain/ProjectDomain';
import {ASSET_FILE_REQUIREMENT_COLLECTION, FILE_REQUIREMENT_COLLECTION} from '../../requirements/domain/config';
import {ASSET_TASK_COLLECTION, TASK_COLLECTION} from '../domain/config';
import {TaskType} from '../enums/TaskType';


const BASE_OPTIONS = {
  ...BASE_REDUX_LIST_OPTIONS,
  useFilterOptions: true,
  serializeFilterOptions,
  defaultFilterOptions: {},
};

export function buildFilesZipFactory(options) {
  // options.serializeFilterOptions = (initialState, filterOptions) => JSON.stringify(filterOptions)
  return new AjaxFactory(buildFilesZipRequestOptions(options));
}

/**
 * @param options
 * @return {AjaxFactoryOptions}
 */
export function buildFilesZipRequestOptions(options) {
  return {
    crossDomain: options.crossDomain,
    method: HttpMethod.GET,
    useBody: false,
    url: getFilesZipUrl(options),
    getToken: options.getToken,
    responseType: request => 'json',
  };
}

/**
 * @param options
 * @return {Function}
 */
export function getFilesZipUrl(options) {
  /**
   * @param {ListPageRequest} request
   */
  return function getReportUrlCurried(request) {
    const params = getPageFilterParams(options, request);
    return `${options.baseApiUrl}/${options.collection}/${request.id}/zip-files?${params}`;
  };
}

FilesDownloadService.$inject = [
  '$log',
  '$download',
  '$toast',
  '$translate',
  '$ngRedux',
  'NotificationService',
  'AuthService',
  'AwsS3MultiFileService',
];

export function FilesDownloadService(
  $log,
  $download,
  $toast,
  $translate,
  $ngRedux,
  NotificationService,
  AuthService,
  AwsS3MultiFileService,
) {

  const projectFilesFactory = buildFilesZipFactory({
    ...BASE_OPTIONS,
    collection: PROJECT_COLLECTION,
  });

  const assetProjectFilesFactory = buildFilesZipFactory({
    ...BASE_OPTIONS,
    collection: ASSET_PROJECT_COLLECTION,
  });

  const projectTaskFilesFactory = buildFilesZipFactory({
    ...BASE_OPTIONS,
    collection: TASK_COLLECTION,
  });

  const assetTaskFilesFactory = buildFilesZipFactory({
    ...BASE_OPTIONS,
    collection: ASSET_TASK_COLLECTION,
  });

  const projectRequirementFilesFactory = buildFilesZipFactory({
    ...BASE_OPTIONS,
    collection: FILE_REQUIREMENT_COLLECTION,
  });

  const assetRequirementFilesFactory = buildFilesZipFactory({
    ...BASE_OPTIONS,
    collection: ASSET_FILE_REQUIREMENT_COLLECTION,
  });

  const defaultFilterData = {
    filterOptions: {
      isArchived: false,
    },
  };

  this.downloadProjectFiles = downloadProjectFiles;
  this.downloadMilestoneFiles = downloadMilestoneFiles;
  this.downloadTaskFiles = downloadTaskFiles;
  this.downloadRequirementFiles = downloadRequirementFiles;

  function downloadProjectFiles(projectId, projectName, taskType) {
    if (!projectId) {
      return Promise.resolve(null);
    }

    const filterData = {
      ...defaultFilterData,

      filterOptions: {
        ...defaultFilterData.filterOptions,
        useTaskFolders: true,
      },
    };

    let request;
    if (taskType === TaskType.ASSET) {
      request = assetProjectFilesFactory.sendRequest({
        id: projectId,
        filterData: filterData,
      }, $ngRedux.getState());

    } else {
      request = projectFilesFactory.sendRequest({
        id: projectId,
        filterData: filterData,
      }, $ngRedux.getState());
    }

    return __downloadFiles(request.toPromise(), 'Zipping ' + projectName + ' files');
  }

  function downloadMilestoneFiles(projectId, milestoneId, projectName, taskType) {
    if (!projectId) {
      return Promise.resolve(null);
    }

    const filterData = {
      ...defaultFilterData,

      filterOptions: {
        ...defaultFilterData.filterOptions,
        useTaskFolders: true,
        milestone: milestoneId,
      },
    };

    let request;
    if (taskType === TaskType.ASSET) {
      request = assetProjectFilesFactory.sendRequest({
        id: projectId,
        filterData: filterData,
      }, $ngRedux.getState());

    } else {
      request = projectFilesFactory.sendRequest({
        id: projectId,
        filterData: filterData,
      }, $ngRedux.getState());
    }

    return __downloadFiles(request.toPromise(), 'Zipping ' + projectName + ' files');
  }

  function downloadTaskFiles(taskId, taskName, taskType) {
    if (!taskId) {
      return Promise.resolve(null);
    }

    let request;
    if (taskType === TaskType.ASSET) {
      request = assetTaskFilesFactory.sendRequest({
        id: taskId,
        filterData: defaultFilterData,
      }, $ngRedux.getState());

    } else {
      request = projectTaskFilesFactory.sendRequest({
        id: taskId,
        filterData: defaultFilterData,
      }, $ngRedux.getState());
    }

    return __downloadFiles(request.toPromise(), 'Zipping ' + taskName + ' files');
  }

  function downloadRequirementFiles(requirementId, requirementName, taskType) {
    if (!requirementId) {
      return Promise.resolve(null);
    }

    let request;
    if (taskType === TaskType.ASSET) {
      request = assetRequirementFilesFactory.sendRequest({
        id: requirementId,
        filterData: defaultFilterData,
      }, $ngRedux.getState());

    } else {
      request = projectRequirementFilesFactory.sendRequest({
        id: requirementId,
        filterData: defaultFilterData,
      }, $ngRedux.getState());
    }

    return __downloadFiles(request.toPromise(), 'Zipping ' + requirementName + ' files');
  }

  function __downloadFiles(request, name) {
    const noteDeferred = Promise.defer();

    // TODO(i18n)
    NotificationService.addNotification({
      name: name,
      removal: NotificationService.REMOVAL.ALWAYS,
      promise: noteDeferred.promise,
    });

    return request
      .then(function (ajaxResponse) {
        const zipResult = ajaxResponse.response;
        $log.debug('FilesDownloadService.__downloadFiles(zipResult)', zipResult);

        if (ajaxResponse.status === 204) {
          // TODO: display this in the notification panel instead and do not remove the notification automatically in this case
          $toast.showI18nMessage({messageId: 'files.not.found.message', type: $toast.TOAST_TYPES.info});
          return $translate('files.not.found.message').then(function (msg) {
            noteDeferred.resolve(msg);
            return msg;
          });

        } else if (ajaxResponse.status === 201) {
          $log.debug('FilesDownloadService.__downloadFiles(response) code 201, requesting with backoff');
          return startZipTaskObserver(zipResult, noteDeferred);

        } else if (ajaxResponse.status === 200 && zipResult.url) {
          noteDeferred.resolve(null);
          $log.debug('FilesDownloadService.__downloadFiles(response) code 200, file is ready');
          return $download.promptDownload(zipResult.url, zipResult.filename);

        } else if (ajaxResponse.status === 200 && zipResult.zipRef) {
          noteDeferred.resolve(null);
          $log.debug('FilesDownloadService.__downloadFiles(response) code 200, redirecting to zip service');

          if (isCordovaBuild()) {
            const url = `${ZIP_API_URL}/files/?ref=${zipResult.zipRef}&downloadas=${zipResult.filename}`;
            return $download.promptDownload(url, zipResult.filename);
          } else {
            window.open(`${ZIP_API_URL}/files/?ref=${zipResult.zipRef}&downloadas=${zipResult.filename}`, '_blank');
            return Promise.resolve(null);
          }

        } else {
          return $translate('files.request.error.message').then(function (msg) {
            noteDeferred.reject(msg);
            return Promise.reject(msg);
          });
        }

      }, function forwardRequestError(error) {
        console.warn('FilesDownloadService.__downloadFiles failed');
        console.error(error);
        noteDeferred.reject(get(error, 'response.message') ? error.response.message : error);
        return Promise.reject(error);
      });
  }

  function startZipTaskObserver(zipResult, noteDeferred) {
    try {
      return AwsS3MultiFileService.create(zipResult, noteDeferred);
    } catch (e) {
      console.warn('FilesDownloadService:AwsS3MultiFileService.create failed');
      console.error(e);
      noteDeferred.reject(e);
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/tasks/services/FilesDownloadService.js